import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBarRef, MatSnackBarVerticalPosition, MatSnackBarHorizontalPosition } from '@angular/material/snack-bar';
import { Observable, take, map } from 'rxjs';
import { DialogComponent } from '../components/dialog/dialog.component';
import { SnackDeleteComponent } from '../components/snack-delete/snack-delete.component';
import { AppSnackBarService } from '../../core/services/app-snack-bar.service';

export interface OptionsDialogOpen {
  title: string, message: string, cancelText: string, confirmText: string, content?: any,
  /**
   * Interpolation params for `title`/`message` when either is an i18n key carrying placeholders
   * (`{{count}}` and the like). Backward-compatible: an omitted `params` renders exactly as
   * before, since {@link DialogComponent}'s template passes it straight to `| translate` and an
   * `undefined` pipe argument is a no-op. Prefer this over pre-translating the string yourself
   * with `TranslateService.instant` — the same anti-pattern {@link DialogService.openSnack}'s own
   * JSDoc warns against one method over: an already-translated sentence handed in as `title`/
   * `message` is looked up a second time by the dialog's own `| translate`, which turns user data
   * into a translation-key path.
   */
  params?: { [key: string]: unknown },
}

/**
 * Named form of {@link DialogService.openSnack}'s trailing arguments. Prefer it over the
 * positional overload whenever a call needs anything past {@code icon} — the positional form
 * bottoms out at six arguments and forces {@code undefined, undefined} placeholders just to reach
 * {@code params}, which reads as noise and is easy to mis-order.
 */
export interface SnackOptions {
  icon?: string;
  /** Auto-dismiss delay in ms. Still scaled by the user's `display.toastDuration` preference. */
  time?: number;
  /**
   * Open the toast with NO auto-dismiss — it stays until the user clicks it (the click-to-dismiss
   * {@link AppSnackBarService} wires for every sticky toast). For a message that is the only
   * explanation the operator will ever get: a lookup that failed to load, a refused delete.
   *
   * <p>It has to be asked for EXPLICITLY. Omitting {@code time} does not make a toast sticky — the
   * default below is 4000ms — so four toasts written as "no `time`, so it stays until dismissed"
   * shipped as 4-second ones (2s under the {@code short} preset), each behind a per-round latch
   * that then said nothing for the rest of the round. Sticky is also the one duration the
   * preference cannot scale away: {@code scaleDuration} maps an absent base to {@code undefined}
   * under every preset, which is exactly how an error toast is already sticky everywhere.
   */
  sticky?: boolean;
  verticalPosition?: MatSnackBarVerticalPosition;
  horizontalPosition?: MatSnackBarHorizontalPosition;
  /** Interpolation params for {@code option} when it is an i18n key carrying placeholders. */
  params?: { [key: string]: unknown };
}

@Injectable({
  providedIn: 'root'
})
export class DialogService {

  constructor(private dialog: MatDialog, private snack: AppSnackBarService) {}
  snackBarRef!: MatSnackBarRef<SnackDeleteComponent>;

  /**
   * Open a toast. {@code option} is an i18n KEY — the snack component runs it through the
   * translate pipe. Pass {@code params} for a key with interpolation placeholders instead of
   * pre-translating the string yourself: an already-translated sentence handed in as
   * {@code option} is looked up a second time, which turns user data (a warehouse name, a
   * customer name) into a translation-key path.
   *
   * <p>Returns the {@link MatSnackBarRef} it opened. Almost every caller ignores it; take it when the
   * toast needs to outlive an overlay teardown — see
   * {@code OverlayTeardownService.exemptSnackBarFromTeardown}.
   */
  public openSnack(option: any, iconOrOptions?: string | SnackOptions, time?: number, verticalPosition?: MatSnackBarVerticalPosition, horizontalPosition?: MatSnackBarHorizontalPosition, params?: { [key: string]: unknown }): MatSnackBarRef<SnackDeleteComponent> {
    // ONE signature with a union second argument, deliberately NOT a pair of declared overloads.
    // Jasmine derives the expected-args tuple of `toHaveBeenCalledWith` from the LAST overload of
    // a spied method, so declaring `openSnack(option, options: SnackOptions)` last makes every
    // existing positional assertion across the suite fail to compile. A union keeps both call
    // shapes type-checking. The cost: passing named options AND trailing positional arguments
    // (`openSnack('k', {icon}, 5000)`) compiles while the 5000 is ignored — pick one form.
    //
    // `typeof === 'object'` rather than a truthiness check: the positional form's second argument
    // is always a string (an icon name), so an object there is unambiguously the named form.
    const opts: SnackOptions = iconOrOptions != undefined && typeof iconOrOptions === 'object'
      ? iconOrOptions
      : { icon: iconOrOptions, time, verticalPosition, horizontalPosition, params };

    const ref = this.snack.openFromComponent(SnackDeleteComponent, {
      data: {
        content: option,
        icon: opts.icon,
        params: opts.params
      },
      verticalPosition: opts.verticalPosition ? opts.verticalPosition : 'bottom',
      horizontalPosition: opts.horizontalPosition ? opts.horizontalPosition : 'center',

      // `undefined` is what makes a toast sticky all the way down: `AppSnackBarService.scale`
      // hands it to `resolveDuration`, which returns `undefined` for an absent base under every
      // preset, and a sticky toast gets click-to-dismiss wired. `sticky` wins over `time` — a call
      // passing both is asking for the two things at once, and the explicit flag is the answer.
      duration: opts.sticky ? undefined : (opts.time ? opts.time : 4000),
      panelClass: 'toastConfig',

    });
    // Return the LOCAL ref, not the field. The field is shared mutable state kept for closeBarRef()
    // and legacy callers; handing it back would return whatever toast is current at read time rather
    // than the one this call opened — the same cross-call race `open()` documents avoiding with a
    // local const. The local can only ever be this call's toast.
    this.snackBarRef = ref;
    return ref;
  }

  public closeBarRef() {
    this.snack.dismiss();
  }

  /**
   * Open a confirm dialog and return the dialog's {@link MatDialogRef.afterClosed} observable,
   * bound to the SPECIFIC dialog this call opened. The returned Observable emits once:
   * {@code true} on confirm, {@code false} on cancel/ESC, and {@code undefined} when the dialog
   * is dismissed via the backdrop.
   *
   * <p>Always consume the result through this return value
   * ({@code this.dialog.open(...).subscribe(...)}): it captures the afterClosed observable at
   * open-time, so a later {@code open()} from anywhere else cannot redirect the subscription to a
   * different dialog's result. The dialog ref is intentionally a local const (no shared field),
   * which removes the cross-call race entirely.
   *
   * <p><b>Caveat — the forced-close guard is not reachable from here.</b> Returning only the observable
   * means a caller never sees the {@code MatDialogRef}, so it can use neither
   * {@code OverlayTeardownService.afterUserClosed(ref)} nor {@code wasForceClosed(ref)} (see the
   * "Session teardown closes the overlays" section in CLAUDE.md). A forced close emits
   * {@code undefined} — the same value as a backdrop dismissal — so the FALSY branch of every consumer
   * here also runs on a dead session.
   *
   * <p>That is safe as long as ONE INVARIANT HOLDS: <b>every falsy branch of an {@code open()} consumer
   * does nothing but release state it owns</b> — a {@code DynaConfActionType.RowSelect false} dispatch,
   * reverting a slide-toggle the user just flipped, dropping a CSS flag. No HTTP, no write to
   * user-scoped settings, <b>and no opening of another overlay</b>: {@code afterClosed} fires only when
   * the exit animation finishes, i.e. after {@code closeAll()} has snapshotted {@code openDialogs} and
   * returned and after the teardown has navigated, so a dialog opened from there is not in the snapshot
   * and lands on the login page — the very bug this all exists to fix. That is exactly the "state you
   * own" which must run on EVERY close anyway, so
   * a forced close is indistinguishable from a cancel by design. Stated as an invariant rather than a
   * consumer list on purpose: this helper has well over a dozen call sites and any enumeration goes
   * stale at the next one. <b>If your falsy branch needs to do more than that, don't widen this helper
   * — open the dialog with {@code MatDialog.open(DialogComponent, …)} yourself</b> (same config as
   * below) so you hold the ref the guard needs.
   */
  public open(options : OptionsDialogOpen ): Observable<any> {
    const dialogRef = this.dialog.open(DialogComponent, {
      data: {
        title: options.title,
        message: options.message,
        cancelText: options.cancelText,
        confirmText: options.confirmText,
        content: options.content,
        params: options.params
      },
      hasBackdrop: true
    });
    return dialogRef.afterClosed().pipe(take(1), map(res => res));
  }
}
